import { Model } from "@alphascriptsolutions/client-core";

export const getSelectedFeatures = (model: Model) => {
  const getFeatureValue = (buttonNames: string[]) => buttonsValue(buttonNames, model);

  const selectedFeatures: string[] = [];

  // List of feature buttons to check
  const featureButtons = [
    { names: ["percentageChangeChart", "actualTradedValueChart"], feature: "percentage-change-chart" },
    { names: ["addCustomBuiltIndices", "noCustomBuiltIndices"], feature: "custom-built-indices" },
    { names: ["addConstantLineSelection", "noConstantLineSelection"], feature: "constant-value-line" },
    { names: ["addsummaryTable", "nosummaryTable"], feature: "summary-table" },
    { names: ["addSmallLineChart", "nosmallLineChart"], feature: "small-line-chart" },
    { names: ["addCallOutBoxes", "noCallOutBoxes"], feature: "call-out-boxes" },
    { names: ["addBackgroundShading", "noBackgroundShading"], feature: "background-shading" },
    { names: ["addVerticalLine", "noVerticalLine"], feature: "vertical-line" },
  ];

  // Iterate over the buttons and add the selected feature if "Yes"
  featureButtons.forEach(({ names, feature }) => {
    const featureValue = getFeatureValue(names);
    if (featureValue === names[0]) {
      selectedFeatures.push(feature);
    }
  });

  return selectedFeatures;
};

export const getSelectedFeatureTradingData = (model: Model) => {
  const getFeatureValue = (buttonNames: string[]) => buttonsValue(buttonNames, model);

  const names = ["addsecondaryVerticalAxisColumnChart", "nosecondaryVerticalAxisColumnChart"];
  const featureValue = getFeatureValue(names);
  return featureValue === names[0];
};

export const buttonsValue = (buttonNames: string[], currentScroller: any): string | null => {
  if (!currentScroller) return null;
  let selectedButton: string | undefined = undefined;

  buttonNames.forEach((name) => {
    const button = currentScroller.getByName(name);
    if (button) {
      if (button.value) {
        selectedButton = button.name;
      }
    }
  });
  return selectedButton;
};

export function transformGlobalState(globalState: any): Record<string, any> {
  const result: Record<string, any> = {};

  // ─── Companies ─────────────────────────────────────────────
  const companies: any[] = [];

  const primary = globalState?.primaryCompany;
  if (primary && typeof primary === "object") {
    companies.push({
      ...primary,
      ticker: primary.ticker,
      category: "primary",
      dataType: globalState.dataType ?? "Adjusted_Closing_Share_Price",
    });
  }

  const peers = globalState?.peerCompanies;
  if (Array.isArray(peers)) {
    const peerList = peers.map((peer) => ({
      ...peer,
      ticker: peer.ticker,
      category: "peer",
      dataType: globalState.dataType ?? "Adjusted_Closing_Share_Price",
    }));
    companies.push(...peerList);
  }

  if (companies.length > 0) {
    result.companies = companies;
  }

  // ─── Time Period & Currency ────────────────────────────────
  const { timePeriod } = globalState;
  if (timePeriod) {
    result.startDate = timePeriod.startDate;
    result.endDate = timePeriod.endDate;
  }

  result.currency = globalState.currency;

  // ─── Percentage Change Chart ───────────────────────────────
  const percentageForm = globalState?.percentageChangeChartForm;
  result.percentageChangeChart = {};

  if (percentageForm) {
    const chart = result.percentageChangeChart;

    if (percentageForm.indexStartingPoint === "Share Price") {
      chart.indexStartingPoint = "share-price";
    } else if (percentageForm.indexStartingPoint === "Value") {
      chart.indexStartingPoint = "value";
      chart.value = String(percentageForm.value);
    }
  }

  // ─── Constant Value Lines ──────────────────────────────────
  const constantForm = globalState?.constantValueLineForm;
  result.constantValueLines = Array.isArray(constantForm)
    ? constantForm.map((item) => {
        const isTimeSeries = item?.derivedDataCalculationApproach === "time-series";

        return {
          ...item,
          ...(isTimeSeries &&
            typeof item?.calculation === "string" && {
              calculation: item.calculation.toLowerCase(),
            }),
          ...(item.value !== undefined && { value: String(item.value) }),
          ...(item.company?.id && { company: item.company.id }),
        };
      })
    : [];

  // ─── Custom Built Indices ──────────────────────────────────
  const customIndices = globalState?.customBuiltIndicesForm;
  result.customBuiltIndex = Array.isArray(customIndices)
    ? customIndices.map((index) => {
        const updatedConstituents = index.constituents.map((c) => {
          const processedTicker = c.ticker?.split(":")[1] || c.ticker;
          return {
            ...c,
            ticker: processedTicker,
          };
        });

        return {
          ...index,
          contituents: updatedConstituents,
        };
      })
    : [];

  // ─── Vertical Lines ────────────────────────────────────────
  result.verticalLines = Array.isArray(globalState?.verticalLineForm) ? globalState.verticalLineForm : [];

  // ─── Background Shading ────────────────────────────────────
  result.shadingPeriods = Array.isArray(globalState?.backgroundShadingForm) ? globalState.backgroundShadingForm : [];

  // ─── Call Out Boxes ────────────────────────────────────────
  const callOuts = globalState?.callOutBoxesForm;
  result.callOutBoxes = Array.isArray(callOuts)
    ? callOuts.map((item) => ({
        ...item,
        company: item.company?.id || item.company,
      }))
    : [];

  // ─── Summary Table ─────────────────────────────────────────
  const summary = globalState?.summaryTableForm;
  result.summaryTable =
    summary && typeof summary === "object"
      ? {
          ...summary,
          companyIndices: summary.companyIndices?.map((c) => c.id) || [],
          customBuiltIndexes: summary.customBuiltIndexes?.map((c) => c.id) || [],
        }
      : {};

  // ─── Small Line Chart ──────────────────────────────────────
  const lineChart = globalState?.smallLineChartForm;
  result.smallLineChart =
    lineChart && typeof lineChart === "object"
      ? {
          ...lineChart,
          companyIndices: lineChart.companyIndices?.map((c) => c.id) || [],
          customBuiltIndexes: lineChart.customBuiltIndexes?.map((c) => c.id) || [],
          constantValueLines: lineChart.constantValueLines?.map((c) => c.id) || [],
        }
      : {};

  return result;
}

type Metadata = {
  chartTitle: {
    enabled: false,
    value: "",
  },
  chartSubTitle: {
    enabled: false,
    value: "",
  },
  source: {
    enabled: false,
    value: "",
    positioning: "",
  },
  notes: {
    enabled: false,
    value: "",
    positioning: "",
  },
};

export function addFieldsToData({
  chartObject,
  customFormatId,
  customPlaceholder = {},
  titlePlaceholderShape = {},
  subTitlePlaceholderShape = {},  
  chartSubtitleRight = false,
  metadata,
  tradingVolume = false,
}) {
  return {
    chartType: "sharePricePerformanceLineChart",
    ...chartObject,
    tradingVolume: tradingVolume,
    formatId: customFormatId,
    placeholder: customPlaceholder,
    titlePlaceholder: titlePlaceholderShape,
    subTitlePlaceholder: subTitlePlaceholderShape,
    subtitleRight: chartSubtitleRight,
    metadata: metadata,
  };
}
